import { PHOTO_MAX_BYTES } from '@lbc/shared';

export const PHOTO_HARD_LIMIT_BYTES = PHOTO_MAX_BYTES;

export interface PreparedPhoto {
  blob: Blob;
  mimeType: 'image/jpeg' | 'image/png' | 'image/webp';
  width: number;
  height: number;
}

export async function preparePhoto(file: File): Promise<PreparedPhoto> {
  if (!file.type.startsWith('image/')) {
    throw new Error('Bitte wähle eine Bilddatei aus.');
  }
  const bitmap = await createBitmap(file);
  try {
    const scale = Math.min(1, 3_200 / Math.max(bitmap.width, bitmap.height));
    let width = Math.max(1, Math.round(bitmap.width * scale));
    let height = Math.max(1, Math.round(bitmap.height * scale));
    let quality = 0.86;
    let blob = await render(bitmap, width, height, quality);
    while (blob.size > PHOTO_HARD_LIMIT_BYTES && quality > 0.5) {
      quality -= 0.08;
      blob = await render(bitmap, width, height, quality);
    }
    while (blob.size > PHOTO_HARD_LIMIT_BYTES && Math.max(width, height) > 1_600) {
      width = Math.max(1, Math.round(width * 0.8));
      height = Math.max(1, Math.round(height * 0.8));
      quality = 0.78;
      blob = await render(bitmap, width, height, quality);
    }
    if (blob.size > PHOTO_HARD_LIMIT_BYTES) {
      throw new Error('Das Foto ist auch nach der Verkleinerung noch größer als 5 MB.');
    }
    return { blob, mimeType: 'image/jpeg', width, height };
  } finally {
    bitmap.close();
  }
}

async function createBitmap(file: File): Promise<ImageBitmap> {
  const createImageBitmapFn = (
    globalThis as typeof globalThis & {
      createImageBitmap?: (
        image: ImageBitmapSource,
        options?: ImageBitmapOptions,
      ) => Promise<ImageBitmap>;
    }
  ).createImageBitmap;
  if (createImageBitmapFn === undefined) {
    throw new Error('Dieser Browser kann Fotos nicht verarbeiten.');
  }
  return createImageBitmapFn(file, { imageOrientation: 'from-image' });
}

function render(bitmap: ImageBitmap, width: number, height: number, quality: number): Promise<Blob> {
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const context = canvas.getContext('2d');
  if (context === null) throw new Error('Das Foto konnte nicht verarbeitet werden.');
  context.drawImage(bitmap, 0, 0, width, height);
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => (blob === null ? reject(new Error('Das Foto konnte nicht verarbeitet werden.')) : resolve(blob)),
      'image/jpeg',
      quality,
    );
  });
}
